import { inject, Injectable, Injector } from '@angular/core';
import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ImageOverlayContentComponent } from './image-overlay-content.component';
import { ESCAPE, hasModifierKey } from '@angular/cdk/keycodes';
import { isFunction } from '@pct-ts/typeguard';

@Injectable()
export class ImagePreviewService {
  protected readonly overlay = inject(Overlay);
  protected readonly injector = inject(Injector);

  protected overlayRef: OverlayRef | undefined = undefined;

  openImagePreview({
    file,
    attachListeners,
  }: {
    file: File;
    attachListeners?: (overlayRef: OverlayRef) => void;
  }): void {
    const config = new OverlayConfig({
      hasBackdrop: true,
      disposeOnNavigation: true,
      backdropClass: 'dark-backdrop',
      panelClass: 'image-overlay-panel',
      positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
      scrollStrategy: this.overlay.scrollStrategies.block(),
    });

    this.overlayRef = this.overlay.create(config);

    const imageOverlayPortal = new ComponentPortal(
      ImageOverlayContentComponent,
      null,
      Injector.create({
        parent: this.injector,
        providers: [
          {
            provide: OverlayRef,
            useValue: this.overlayRef,
          },
        ],
      }),
    );

    const componentRef = this.overlayRef.attach(imageOverlayPortal);

    componentRef.instance.file.set(file);

    this.attachKeyboardListeners();
    this.attachBackdropListeners();
    if (isFunction(attachListeners)) {
      attachListeners(this.overlayRef);
    }
  }

  protected attachKeyboardListeners(): void {
    if (this.overlayRef !== undefined) {
      this.overlayRef.keydownEvents().subscribe(event => {
        if (event.keyCode === ESCAPE && !hasModifierKey(event)) {
          this.closeOverlay();
        }
      });
    }
  }

  protected attachBackdropListeners(): void {
    if (this.overlayRef !== undefined) {
      this.overlayRef.backdropClick().subscribe(this.closeOverlay.bind(this));
    }
  }

  closeOverlay(): void {
    if (this.overlayRef !== undefined) {
      this.overlayRef.dispose();
      this.overlayRef = undefined;
    }
  }
}
